// src/i18n.js
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import translationEN from '@/locales/en/common.json' // 英文翻译
import translationZH from '@/locales/zh/common.json' // 中文翻译

// 配置语言资源
const resources = {
  en: { translation: translationEN },
  zh: { translation: translationZH },
}

// 规范化语言代码：将 'zh-CN' 映射到 'zh'，其他保持不变
const normalizeLanguage = (lang: string): string => {
  if (lang === 'zh-CN' || lang === 'zh') {
    return 'zh'
  }
  return lang
}

// 从构建时环境变量、localStorage 读取语言设置，如果没有则使用默认值
const getDefaultLanguage = (): string => {
  // 获取构建时注入的语言（如果存在）
  const buildLang = typeof APP_LANG !== 'undefined' && APP_LANG ? normalizeLanguage(APP_LANG) : null

  // 获取 localStorage 中保存的语言
  let savedLang: string | null = null
  try {
    const langFromStorage = localStorage.getItem('language')
    if (langFromStorage) {
      savedLang = normalizeLanguage(langFromStorage)
    }
  } catch (error) {
    // localStorage 可能不可用，忽略错误
  }

  // 确定默认语言：
  // 1. 如果构建时指定了语言，优先使用构建时的语言（首次访问或构建时指定的默认语言）
  // 2. 如果构建时没有指定，但 localStorage 中有值，使用 localStorage 的值
  // 3. 如果都没有，使用默认值 'zh'
  let defaultLang: string
  if (buildLang) {
    defaultLang = buildLang
    // 如果构建时的语言与 localStorage 中的不同，更新 localStorage
    // 这样构建时指定的语言会生效
    if (savedLang !== buildLang) {
      try {
        const langToSave = buildLang === 'zh' ? 'zh-CN' : buildLang
        localStorage.setItem('language', langToSave)
      } catch (error) {
        // localStorage 可能不可用，忽略错误
      }
    }
  } else if (savedLang) {
    defaultLang = savedLang
  } else {
    defaultLang = 'zh'
    // 保存默认语言到 localStorage
    try {
      localStorage.setItem('language', 'zh-CN')
    } catch (error) {
      // localStorage 可能不可用，忽略错误
    }
  }

  return defaultLang
}

// 获取回退语言，优先使用构建时环境变量，否则使用默认值
const getFallbackLanguage = (): string => {
  if (typeof APP_LANG !== 'undefined' && APP_LANG) {
    return normalizeLanguage(APP_LANG)
  }
  return 'zh'
}

// 初始化 i18n
i18n
  .use(initReactI18next) // 集成 React
  .init({
    resources,
    lng: getDefaultLanguage(), // 从构建时环境变量、localStorage 读取或使用默认语言
    fallbackLng: getFallbackLanguage(), // 回退语言，根据构建时环境变量设置
    // 支持语言代码映射：'zh-CN' -> 'zh'
    load: 'languageOnly', // 只加载语言代码，忽略地区代码
    interpolation: { escapeValue: false }, // React 已处理 XSS，无需 escape
  })

// 导出 t 函数（关键：供全局注入使用）
export const t = i18n.t.bind(i18n)

export default i18n
